(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------------ *
   * One controller per section instance.
   * ------------------------------------------------------------------ */
  function Carousel(section) {
    this.el = section;
    this.viewport = section.querySelector('[data-pca-viewport]');
    this.track = section.querySelector('[data-pca-track]');
    if (!this.viewport || !this.track) return;

    this.originals = Array.prototype.slice.call(
      this.track.querySelectorAll('.pca__slide')
    );
    if (this.originals.length === 0) return;

    this.mode = section.dataset.mode === 'paged' ? 'paged' : 'continuous';
    this.autoplay = section.dataset.autoplay !== 'false';
    this.pauseHover = section.dataset.pauseHover !== 'false';
    this.speed = parseFloat(section.dataset.speed) || 45; // px / second
    this.dir = section.dataset.direction === 'right' ? -1 : 1;
    this.pagedInterval = (parseFloat(section.dataset.pagedInterval) || 4) * 1000;
    this.resumeDelay = 1200; // ms after a manual interaction

    this.clones = [];
    this.pauses = {};
    this.pos = 0;
    this.unit = 0;
    this.lastTime = null;
    this.raf = null;
    this.pagedTimer = null;
    this.resumeTimer = null;

    // No auto-motion wanted: leave a clean, swipeable native-scroll row.
    if (!this.autoplay || reduceMotion.matches) {
      this.bindArrows();
      this.bindEditor();
      return;
    }

    this.frame = this.frame.bind(this);
    this.onResize = debounce(this.rebuild.bind(this), 200);

    if (this.mode === 'continuous') {
      this.el.classList.add('pca--ready');
      this.build();
      this.raf = requestAnimationFrame(this.frame);
    } else {
      this.el.classList.add('pca--ready', 'pca--paged');
      this.startPaged();
    }

    this.bindInteractions();
    this.bindArrows();
    this.bindEditor();
    window.addEventListener('resize', this.onResize);
  }

  /* --------------------------------------------------- build / measure */

  Carousel.prototype.build = function () {
    var self = this;
    // clear previous clones
    this.clones.forEach(function (c) { c.remove(); });
    this.clones = [];

    var viewportW = this.viewport.clientWidth;

    var appendSet = function () {
      self.originals.forEach(function (el) {
        var c = el.cloneNode(true);
        c.classList.add('pca__slide--clone');
        c.setAttribute('aria-hidden', 'true');
        c.removeAttribute('data-pca-block');
        // keep clones clickable by mouse, but out of tab + a11y order
        Array.prototype.forEach.call(
          c.querySelectorAll('a, button, [tabindex]'),
          function (f) { f.setAttribute('tabindex', '-1'); }
        );
        self.track.appendChild(c);
        self.clones.push(c);
      });
    };

    appendSet();
    // repeat distance = gap between an original and its first clone
    this.unit = this.clones[0].offsetLeft - this.originals[0].offsetLeft;

    // Ensure there is always at least one viewport of content past the wrap
    // point so the loop never reveals an empty edge, even for few products.
    var guard = 0;
    while (this.track.scrollWidth < this.unit + viewportW + 4 && guard < 30) {
      appendSet();
      guard++;
    }

    this.pos = this.dir < 0 ? this.unit : 0;
    this.viewport.scrollLeft = this.pos;
  };

  Carousel.prototype.rebuild = function () {
    if (this.mode !== 'continuous') return;
    var ratio = this.unit ? this.pos / this.unit : 0;
    this.build();
    this.pos = this.unit * ratio;
    this.viewport.scrollLeft = this.pos;
  };

  /* --------------------------------------------------- continuous loop */

  Carousel.prototype.frame = function (now) {
    this.raf = requestAnimationFrame(this.frame);
    if (!this.isPlaying()) {
      this.lastTime = now;
      return;
    }
    if (this.lastTime == null) {
      this.lastTime = now;
      return;
    }
    var dt = (now - this.lastTime) / 1000;
    this.lastTime = now;
    if (dt > 0.1) dt = 0.1; // clamp after a stall / hidden tab

    this.pos += this.dir * this.speed * dt;
    if (this.dir > 0 && this.pos >= this.unit) this.pos -= this.unit;
    else if (this.dir < 0 && this.pos <= 0) this.pos += this.unit;
    this.viewport.scrollLeft = this.pos;
  };

  // Re-anchor to the canonical [0, unit) window. Because the content repeats
  // every `unit`, this correction is visually seamless.
  Carousel.prototype.syncPos = function () {
    if (!this.unit) return;
    var p = this.viewport.scrollLeft % this.unit;
    if (p < 0) p += this.unit;
    this.pos = p;
    this.viewport.scrollLeft = p;
  };

  /* --------------------------------------------------- paged loop */

  Carousel.prototype.startPaged = function () {
    var self = this;
    clearInterval(this.pagedTimer);
    this.pagedTimer = setInterval(function () {
      if (!self.isPlaying()) return;
      var vp = self.viewport;
      var slide = self.track.querySelector('.pca__slide');
      var step = slide
        ? slide.getBoundingClientRect().width + gapOf(self.track)
        : vp.clientWidth * 0.8;
      var max = vp.scrollWidth - vp.clientWidth - 2;
      var target = vp.scrollLeft + step;
      if (target >= max) target = 0;
      vp.scrollTo({ left: target, behavior: 'smooth' });
    }, this.pagedInterval);
  };

  /* --------------------------------------------------- play / pause */

  Carousel.prototype.isPlaying = function () {
    for (var k in this.pauses) {
      if (this.pauses[k]) return false;
    }
    return true;
  };

  Carousel.prototype.pause = function (reason) {
    this.pauses[reason] = true;
  };

  Carousel.prototype.resume = function (reason) {
    delete this.pauses[reason];
    if (this.mode === 'continuous' && this.isPlaying()) {
      this.syncPos();
      this.lastTime = null;
    }
  };

  Carousel.prototype.resumeSoon = function (reason) {
    var self = this;
    clearTimeout(this.resumeTimer);
    this.resumeTimer = setTimeout(function () {
      self.resume(reason);
    }, this.resumeDelay);
  };

  /* --------------------------------------------------- interactions */

  Carousel.prototype.bindInteractions = function () {
    var self = this;
    var vp = this.viewport;

    if (this.pauseHover) {
      this.el.addEventListener('mouseenter', function () { self.pause('hover'); });
      this.el.addEventListener('mouseleave', function () { self.resume('hover'); });
    }

    // Keyboard users get time to read while a card is focused.
    this.el.addEventListener('focusin', function () { self.pause('focus'); });
    this.el.addEventListener('focusout', function () { self.resume('focus'); });

    // Pointer drag (mouse / pen). Touch uses native scrolling below.
    var dragging = false;
    var startX = 0;
    var startScroll = 0;

    vp.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch') return; // let native touch-scroll handle it
      dragging = true;
      startX = e.clientX;
      startScroll = vp.scrollLeft;
      self.pause('drag');
      vp.setPointerCapture(e.pointerId);
      vp.style.cursor = 'grabbing';
    });
    vp.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      vp.scrollLeft = startScroll - (e.clientX - startX);
    });
    var endDrag = function (e) {
      if (!dragging) return;
      dragging = false;
      vp.style.cursor = '';
      if (e && e.pointerId != null && vp.hasPointerCapture(e.pointerId)) {
        vp.releasePointerCapture(e.pointerId);
      }
      self.resumeSoon('drag');
    };
    vp.addEventListener('pointerup', endDrag);
    vp.addEventListener('pointercancel', endDrag);

    // Native touch / trackpad scrolling: pause while interacting, resume after.
    vp.addEventListener('touchstart', function () { self.pause('touch'); }, { passive: true });
    vp.addEventListener('touchend', function () { self.resumeSoon('touch'); }, { passive: true });
    vp.addEventListener('wheel', function (e) {
      if (Math.abs(e.deltaX) < Math.abs(e.deltaY)) return; // vertical page scroll
      self.pause('wheel');
      self.resumeSoon('wheel');
    }, { passive: true });

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) self.pause('hidden');
      else self.resume('hidden');
    });
  };

  /* --------------------------------------------------- arrows */

  Carousel.prototype.bindArrows = function () {
    var self = this;
    var prev = this.el.querySelector('[data-pca-prev]');
    var next = this.el.querySelector('[data-pca-next]');
    if (!prev && !next) return;

    var nudge = function (sign) {
      var slide = self.track.querySelector('.pca__slide');
      var step = slide
        ? slide.getBoundingClientRect().width + gapOf(self.track)
        : self.viewport.clientWidth * 0.8;
      self.pause('arrow');
      self.viewport.scrollBy({
        left: sign * step,
        behavior: reduceMotion.matches ? 'auto' : 'smooth'
      });
      self.resumeSoon('arrow');
    };

    if (prev) prev.addEventListener('click', function () { nudge(-1); });
    if (next) next.addEventListener('click', function () { nudge(1); });
  };

  /* --------------------------------------------------- theme editor */

  Carousel.prototype.bindEditor = function () {
    if (!window.Shopify || !Shopify.designMode) return;
    var self = this;

    this.el.addEventListener('shopify:block:select', function (e) {
      self.pause('editor');
      var slide = self.el.querySelector(
        '[data-pca-block="' + e.detail.blockId + '"]'
      );
      if (slide) {
        slide.scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
          block: 'nearest'
        });
      }
    });
    this.el.addEventListener('shopify:block:deselect', function () {
      self.resume('editor');
    });
    this.el.addEventListener('shopify:section:select', function () {
      self.pause('editor');
    });
    this.el.addEventListener('shopify:section:deselect', function () {
      self.resume('editor');
    });
  };

  Carousel.prototype.destroy = function () {
    if (this.raf) cancelAnimationFrame(this.raf);
    if (this.pagedTimer) clearInterval(this.pagedTimer);
    if (this.resumeTimer) clearTimeout(this.resumeTimer);
    if (this.onResize) window.removeEventListener('resize', this.onResize);
    this.clones.forEach(function (c) { c.remove(); });
    this.clones = [];
  };

  /* --------------------------------------------------- helpers */

  function gapOf(track) {
    var g = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap);
    return isNaN(g) ? 20 : g;
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  /* --------------------------------------------------- bootstrapping */

  function init(root) {
    (root || document).querySelectorAll('.pca[data-pca-viewport-host]').forEach(function (section) {
      if (section.pcaInstance) return;
      section.pcaInstance = new Carousel(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // Shopify theme-editor lifecycle.
  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector
      ? e.target.querySelector('.pca[data-pca-viewport-host]')
      : null;
    if (e.target.matches && e.target.matches('.pca[data-pca-viewport-host]')) {
      section = e.target;
    }
    if (section) {
      if (section.pcaInstance && section.pcaInstance.destroy) {
        section.pcaInstance.destroy();
      }
      section.pcaInstance = new Carousel(section);
    }
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var section = e.target.querySelector
      ? e.target.querySelector('.pca[data-pca-viewport-host]')
      : null;
    if (e.target.matches && e.target.matches('.pca[data-pca-viewport-host]')) {
      section = e.target;
    }
    if (section && section.pcaInstance && section.pcaInstance.destroy) {
      section.pcaInstance.destroy();
      section.pcaInstance = null;
    }
  });
})();